export const DOC_FILES_FIELD_ID = 'files';

// Extensions accepted by the project docs upload. Must stay in sync with the docs service manifest.
export const DOC_UPLOAD_FILE_TYPES = ['pdf', 'docx', 'xlsx', 'xlsm', 'csv', 'tsv'];

export const DOC_FILES_OPTION_IDS = {
    ORGANIZATION_DOCS: 'organizationDocs',
    PROJECT_DOCS: 'projectDocs',
    PROJECT_MODEL: 'projectModel',
    ELEMENT_DOCS: 'elementDocs',
};

// Transient option, present only while an element is selected. Unlike the
// static Files chips it is closable from the match bar (notClosable: false).
export const DOC_ELEMENT_DOCS_OPTION = {
    id: DOC_FILES_OPTION_IDS.ELEMENT_DOCS,
    name: 'Element docs',
    displayName: 'Element docs',
    image: '/images/icon/element-docs.svg',
    notClosable: false,
};

// `doc-files-chip` scopes the monochrome icon styling — see _product-filter.scss.
const DOC_FILES_OPTIONS = {
    [DOC_FILES_OPTION_IDS.ORGANIZATION_DOCS]: {
        id: DOC_FILES_OPTION_IDS.ORGANIZATION_DOCS,
        name: 'Organization docs',
        displayName: 'Organization docs',
        image: '/images/icon/organization.svg',
        className: 'doc-files-chip',
    },
    [DOC_FILES_OPTION_IDS.PROJECT_DOCS]: {
        id: DOC_FILES_OPTION_IDS.PROJECT_DOCS,
        name: 'Project docs',
        displayName: 'Project docs',
        image: '/images/icon/project-docs.svg',
        className: 'doc-files-chip',
    },
    [DOC_FILES_OPTION_IDS.PROJECT_MODEL]: {
        id: DOC_FILES_OPTION_IDS.PROJECT_MODEL,
        name: 'Project model',
        displayName: 'Project model',
        image: '/images/icon/cube.svg',
        className: 'doc-files-chip',
    },
};

export const getDocFilesOptions = ({ withProjectModel = false, withProjectDocs = true } = {}) => (
    withProjectModel
        ? [
            DOC_FILES_OPTIONS[DOC_FILES_OPTION_IDS.PROJECT_DOCS],
            DOC_FILES_OPTIONS[DOC_FILES_OPTION_IDS.PROJECT_MODEL],
            DOC_FILES_OPTIONS[DOC_FILES_OPTION_IDS.ORGANIZATION_DOCS],
        ]
        : [
            DOC_FILES_OPTIONS[DOC_FILES_OPTION_IDS.ORGANIZATION_DOCS],
            ...(withProjectDocs ? [DOC_FILES_OPTIONS[DOC_FILES_OPTION_IDS.PROJECT_DOCS]] : []),
        ]
);

// POST /docs/search scope bucket per chip. "house" includes the project's
// element docs; "houseFile" is accepted by the backend but matches nothing yet.
const DOC_FILES_OPTION_TO_SEARCH_SCOPE = {
    [DOC_FILES_OPTION_IDS.ORGANIZATION_DOCS]: 'organization',
    [DOC_FILES_OPTION_IDS.PROJECT_DOCS]: 'house',
    [DOC_FILES_OPTION_IDS.PROJECT_MODEL]: 'houseFile',
};

export const getDocFilesSearchScope = (selectedFilesIds = []) => ([
    ...new Set(selectedFilesIds.map((id) => DOC_FILES_OPTION_TO_SEARCH_SCOPE[id]).filter(Boolean)),
]);

export const DOC_PLACEMENT_SORT_IDS = {
    ORGANIZATION_FIRST: 'organizationFirst',
    PROJECT_FIRST: 'projectFirst',
    ELEMENT_FIRST: 'elementFirst',
};

// POST /docs/search sort allowlist: placement sorts + "<field>" / "-<field>".
const BACKEND_DOC_SORT_VALUES = new Set([
    ...Object.values(DOC_PLACEMENT_SORT_IDS),
    ...['displayName', 'documentNo', 'createdAt', 'updatedAt'].flatMap((field) => [field, `-${field}`]),
]);

export const getBackendDocSortValue = (sortValue) => (
    BACKEND_DOC_SORT_VALUES.has(sortValue) ? sortValue : undefined
);

// The explicit empty strings matter: the text inputs are controlled, and a
// reset to `undefined` would flip them to uncontrolled and keep the typed text.
export const buildDefaultDocsFilterValue = (filesSelection = []) => ({
    [DOC_FILES_FIELD_ID]: filesSelection,
    displayName: '',
    documentNo: '',
});

export const getDocsMatchBarStaticFields = (filesOptions) => ([
    {
        id: DOC_FILES_FIELD_ID,
        label: 'Files',
        subType: 'chips',
        type: 'chips',
        fieldName: DOC_FILES_FIELD_ID,
        notClosable: true,
        options: filesOptions,
    },
    {
        id: 'displayName',
        label: 'Document name',
        subType: 'text',
        placeholder: 'Search by document name',
        type: 'text',
        fieldName: 'displayName',
    },
    {
        id: 'documentNo',
        label: 'Document number',
        subType: 'text',
        placeholder: 'Search by document number',
        type: 'text',
        fieldName: 'documentNo',
    },
]);
